import {
  QUERY_CACHE_TIME,
  REQUEST_ID,
  chains,
  requestTRMScreening,
} from "@sablier/v2-constants";
import { _ } from "@sablier/v2-mixins";
import { QueryClient } from "@tanstack/react-query";
import { getAddress, zeroAddress } from "viem";
import { fetchToken } from "wagmi/actions";
import type { Translate } from "@sablier/v2-locales";
import type { IAddress, IWagmiAddress } from "@sablier/v2-types";
import policy from "./policy";

interface IsBannedProps {
  t: Translate;
  address: IAddress | undefined;
}

interface AddressValidProps {
  t: Translate;
  blacklist?: IAddress[];
  isChecksummed?: boolean;
  isPrefixAllowed?: boolean;
  value: IAddress | undefined;
  whitelist?: IAddress[];
}

export function validateAddress({
  t,
  blacklist,
  whitelist,
  isChecksummed = false,
  isPrefixAllowed = false,
  value,
}: AddressValidProps): string | undefined {
  try {
    if (_.isNilOrEmptyString(value)) {
      return policy.address.missing(t);
    }

    if (!_.isEthereumAddress(value, isPrefixAllowed)) {
      return policy.address.invalid(t);
    }

    if (isChecksummed) {
      const checksummed = _.isPrefixedAddress(value)
        ? "0x".concat(value.split(":")[1])
        : value;

      if (checksummed !== getAddress(checksummed)) {
        throw policy.address.checksummed(t);
      }
    }

    if (!_.isNil(blacklist) && blacklist.length) {
      if (blacklist.map((b) => _.toAddress(b)).includes(_.toAddress(value))) {
        return policy.address.forbidden(t);
      }
    }

    if (!_.isNil(whitelist) && whitelist.length) {
      if (!whitelist.map((b) => _.toAddress(b)).includes(_.toAddress(value))) {
        return policy.address.forbidden(t);
      }
    }
  } catch (error) {
    return policy.address.missing(t, isChecksummed);
  }

  return undefined;
}

interface NameValidProps {
  t: Translate;
  address?: IAddress;
  blacklist?: IAddress[];
  chainId?: number;
  isResolved?: boolean;
  value: string | undefined;
}

export function validateName({
  t,
  address,
  blacklist,
  chainId,
  isResolved = false,
  value,
}: NameValidProps): string | undefined {
  try {
    if (_.isNilOrEmptyString(value)) {
      return policy.ens.missing(t);
    }

    if (!_.isEthereumName(value)) {
      return policy.ens.invalid(t);
    }

    if (chainId) {
      if (chainId !== chains.mainnet.chainId) {
        return policy.ens.supported(t);
      }
    }

    if (isResolved) {
      if (_.isNilOrEmptyString(address)) {
        return policy.ens.address(t, chains.mainnet.name);
      }

      if (!_.isNilOrEmptyString(validateAddress({ t, value: address }))) {
        return policy.ens.address(t, chains.mainnet.name);
      }
    }

    if (!_.isNil(blacklist) && blacklist.length) {
      if (blacklist.map((b) => b.toLowerCase()).includes(value.toLowerCase())) {
        return policy.ens.forbidden(t);
      }
    }
  } catch (error) {
    return policy.ens.invalid(t);
  }

  return undefined;
}

export function validateAccount({
  t,
  address,
  blacklist,
  chainId,
  value,
  isChecksummed,
  isPrefixAllowed,
  isResolved,
  whitelist,
}: AddressValidProps & NameValidProps) {
  if (zeroAddress === value) {
    return policy.address.zero(t);
  }
  if (_.isEthereumName(value)) {
    return validateName({ t, address, blacklist, chainId, isResolved, value });
  } else {
    if (!_.isEthereumAddress(value)) {
      return policy.address.invalid(t, " or ENS");
    }

    return validateAddress({
      t,
      blacklist,
      isChecksummed,
      isPrefixAllowed,
      value,
      whitelist,
    });
  }
}

export async function isSanctioned({ t, address }: IsBannedProps) {
  const queryClient = new QueryClient();
  if (_.isNilOrEmptyString(address)) {
    return undefined;
  }
  try {
    const queryResponse = await queryClient.fetchQuery({
      queryKey: [...REQUEST_ID.trm, { unique: address }],
      queryFn: async () => requestTRMScreening(address),
      staleTime: QUERY_CACHE_TIME,
      cacheTime: QUERY_CACHE_TIME,
    });
    const addressMatch = queryResponse?.find(
      (r) => r.address === address ?? "",
    );
    if (addressMatch) {
      return addressMatch.isSanctioned
        ? policy.address.sanctioned(t)
        : undefined;
    }
  } catch (e) {
    return undefined;
  }
}

export async function isToken({ t, address }: IsBannedProps) {
  try {
    if (_.isNilOrEmptyString(address)) {
      return undefined;
    }
    await fetchToken({
      address: address as IWagmiAddress,
    });
    return policy.address.isToken(t);
  } catch (error) {
    return undefined;
  }
}
